"use client";

import type React from "react";
import { memo, useId, useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import Checkbox from "../ui/checkbox.jsx";

function useFieldId(id?: string) {
  const generatedId = useId();
  return id || generatedId;
}

// OptimizedCheckbox component (NEW)
interface OptimizedCheckboxProps {
  id?: string;
  label?: React.ReactNode;
  checked: boolean;
  onChange?: (checked: boolean) => void;
  required?: boolean;
  readOnly?: boolean;
  description?: string;
  disabled?: boolean;
}

export const OptimizedCheckbox = memo(function OptimizedCheckbox({
  id,
  label,
  checked,
  onChange,
  required,
  readOnly = false,
  description,
  disabled = false,
}: OptimizedCheckboxProps) {
  id = useFieldId(id);
  const handleChange = useCallback(
    (checked: boolean) => {
      onChange?.(checked);
    },
    [onChange],
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Checkbox
          id={id}
          checked={checked}
          disabled={readOnly || disabled}
          onCheckedChange={handleChange}
        />
        <Label htmlFor={id} className="font-normal cursor-pointer">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      </div>
      {description && (
        <p className="text-sm text-slate-500 pl-6 dark:text-muted-foreground">{description}</p>
      )}
    </div>
  );
});

// OptimizedCheckboxGroup component (EXISTING)
interface OptimizedCheckboxGroupProps {
  id?: string;
  label?: React.ReactNode;
  values: string[];
  onChange?: (values: string[]) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  readOnly?: boolean;
  direction?: "row" | "column";
}

export const OptimizedCheckboxGroup = memo(function OptimizedCheckboxGroup({
  id,
  label,
  values,
  onChange,
  options,
  required,
  readOnly = false,
  direction = "column",
}: OptimizedCheckboxGroupProps) {
  id = useFieldId(id);
  const handleToggle = useCallback(
    (checked: boolean, optionValue: string) => {
      if (checked) {
        onChange?.([...values, optionValue]);
      } else {
        onChange?.(values.filter((v) => v !== optionValue));
      }
    },
    [values, onChange],
  );

  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>

      <div
        className={`flex gap-3 ${
          direction === "row" ? "flex-row flex-wrap" : "flex-col"
        }`}
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-center space-x-2">
            <Checkbox
              id={`${id}-${option.value}`}
              checked={values?.includes(option.value)}
              disabled={readOnly}
              onCheckedChange={(checked) =>
                handleToggle(Boolean(checked), option.value)
              }
            />
            <Label
              htmlFor={`${id}-${option.value}`}
              className="font-normal cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
});

// OptimizedInput component (EXISTING)
interface OptimizedInputProps {
  id?: string;
  label?: React.ReactNode;
  value: string | number;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  min?: number;
  max?: number;
  step?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

export const OptimizedInput = memo(function OptimizedInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
  min = 0,
  max = 25,
  step,
  className,
  disabled = false,
  readOnly = false,
}: OptimizedInputProps) {
  id = useFieldId(id);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    },
    [onChange],
  );
  const formattedValue =
    type === "date" && value
      ? new Date(value).toISOString().split("T")[0]
      : (value ?? "");

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        id={id}
        type={type}
        step={step}
        value={formattedValue}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        min={min}
        max={max}
      />
    </div>
  );
});

// OptimizedTextarea component (EXISTING)
interface OptimizedTextareaProps {
  id?: string;
  label?: React.ReactNode;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  className?: string;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export const OptimizedTextarea = memo(function OptimizedTextarea({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  required,
  readOnly = false,
  disabled = false,
  onBlur,
  className,
}: OptimizedTextareaProps) {
  id = useFieldId(id);
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    },
    [onChange],
  );

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Textarea
        id={id}
        value={value}
        onChange={handleChange}
        className={className}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        readOnly={readOnly}
        disabled={disabled}
      />
    </div>
  );
});

// OptimizedSelect component (EXISTING)
interface OptimizedSelectProps {
  id?: string;
  label?: React.ReactNode;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  options: { value: string; label: React.ReactNode }[];
  readOnly?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const OptimizedSelect = memo(function OptimizedSelect({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  options,
  readOnly = false,
  fullWidth = false,
  disabled = false,
}: OptimizedSelectProps) {
  id = useFieldId(id);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Select
        value={value}
        onValueChange={onChange}
        disabled={readOnly || disabled}
      >
        <SelectTrigger id={id} className={fullWidth ? "w-full" : ""}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-card border-slate-200 dark:border-border">
          {options.map((option) => (
            <SelectItem
              className="hover:bg-slate-50 dark:hover:bg-accent"
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});

// OptimizedMultiSelect component (NEW)
interface OptimizedMultiSelectProps {
  showValues?: boolean;
  id: string;
  label: string;
  values: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  readOnly?: boolean;
  description?: string;
}

// In your optimized-form-field.tsx file, replace the existing OptimizedMultiSelect with this:

// export const OptimizedMultiSelect = memo(function OptimizedMultiSelect({
//   id,
//   label,
//   values,
//   onChange,
//   placeholder = "Select options",
//   required,
//   options,
//   readOnly = false,
//   description,
// }: OptimizedMultiSelectProps) {
//   const [isOpen, setIsOpen] = useState(false);
//   const containerRef = useRef<HTMLDivElement>(null);
//   const inputRef = useRef<HTMLInputElement>(null);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (
//         containerRef.current &&
//         !containerRef.current.contains(event.target as Node)
//       ) {
//         setIsOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // Close dropdown on Escape key
//   useEffect(() => {
//     const handleEscape = (event: KeyboardEvent) => {
//       if (event.key === "Escape") {
//         setIsOpen(false);
//         inputRef.current?.blur();
//       }
//     };

//     document.addEventListener("keydown", handleEscape);
//     return () => document.removeEventListener("keydown", handleEscape);
//   }, []);

//   const handleToggle = useCallback(
//     (optionValue: string, e?: React.MouseEvent) => {
//       if (e) {
//         e.preventDefault();
//         e.stopPropagation();
//       }
//       if (readOnly) return;

//       const newValues = values.includes(optionValue)
//         ? values.filter((v) => v !== optionValue)
//         : [...values, optionValue];
//       onChange?.(newValues);
//     },
//     [values, onChange, readOnly],
//   );

//   const removeValue = useCallback(
//     (valueToRemove: string, e: React.MouseEvent) => {
//       e.stopPropagation();
//       if (readOnly) return;
//       onChange?.(values.filter((v) => v !== valueToRemove));
//     },
//     [values, onChange, readOnly],
//   );

//   const handleInputClick = useCallback(() => {
//     if (!readOnly && !isOpen) {
//       setIsOpen(true);
//     }
//   }, [readOnly, isOpen]);

//   // Get selected labels and count
//   const selectedLabels = values
//     .map((value) => options.find((opt) => opt.value === value)?.label)
//     .filter(Boolean);
//   const selectedCount = selectedLabels.length;

//   return (
//     <div className="space-y-2" ref={containerRef}>
//       <Label htmlFor={id}>
//         {label} {required && <span className="text-red-500">*</span>}
//       </Label>

//       <div className="relative">
//         <div
//           className={`min-h-10 px-3 py-2.5 border rounded-lg bg-white dark:bg-card flex items-center justify-between gap-2 transition-all duration-200 shadow-sm ${
//             isOpen
//               ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900 shadow-blue-100/70 dark:shadow-blue-900/20'
//               : 'border-slate-300 dark:border-border hover:border-slate-400 dark:hover:border-primary-border'
//           } ${readOnly ? 'cursor-not-allowed opacity-60 bg-slate-50 dark:bg-surface' : 'cursor-pointer'}`}
//           onClick={handleInputClick}
//         >
//           <div className="flex items-center gap-2 min-w-0">
//             <span className={`text-sm font-medium ${selectedCount > 0 ? 'text-slate-700 dark:text-foreground' : 'text-slate-400 dark:text-muted-foreground'}`}>
//               {/* {selectedCount === 0 ? placeholder : `${selectedCount} ${selectedCount === 1 ? 'item' : 'items'} selected`} */}
//              {
//               placeholder || "Items Selected"
//              }
//             </span>
//             {selectedCount > 0 && (
//               <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-brand/40 dark:text-link">
//                 {selectedCount}
//               </span>
//             )}
//           </div>

//           <input
//             ref={inputRef}
//             type="text"
//             className="sr-only"
//             id={id}
//             readOnly
//             tabIndex={readOnly ? -1 : 0}
//             onFocus={() => !readOnly && setIsOpen(true)}
//             role="combobox"
//             aria-expanded={isOpen}
//             aria-controls={`${id}-options`}
//             aria-haspopup="listbox"
//           />

//           {!readOnly && (
//             <div className="ml-auto flex-shrink-0 pl-2">
//               <svg
//                 className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
//               </svg>
//             </div>
//           )}
//         </div>

//         {/* Dropdown list with checkboxes */}
//         {isOpen && !readOnly && (
//           <div className="absolute z-50 w-full mt-1 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
//             <div className="p-1">
//               {options.map((option) => {
//                 const isSelected = values.includes(option.value);
//                 return (
//                   <div
//                     key={option.value}
//                     role="option"
//                     aria-selected={isSelected}
//                     className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${
//                       isSelected
//                         ? 'bg-blue-50 dark:bg-brand/30 text-blue-900 dark:text-foreground'
//                         : 'hover:bg-slate-50 dark:hover:bg-accent text-slate-700 dark:text-muted-foreground'
//                     }`}
//                     onClick={(e) => handleToggle(option.value, e)}
//                   >
//                     <div
//                       className={`flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
//                         isSelected
//                           ? 'border-blue-500 bg-blue-500 dark:bg-primary dark:border-primary-border'
//                           : 'border-slate-300 dark:border-primary-border'
//                       }`}
//                     >
//                       {isSelected && (
//                         <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
//                         </svg>
//                       )}
//                     </div>
//                     <span className="text-sm">{option.label}</span>
//                     {isSelected && (
//                       <svg className="w-4 h-4 ml-auto text-blue-500 dark:text-link" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                       </svg>
//                     )}
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         )}
//       </div>

//       {description && (
//         <p className="text-sm text-slate-500 dark:text-muted-foreground">{description}</p>
//       )}
//     </div>
//   );
// });

// In your optimized-form-field.tsx file, replace the existing OptimizedMultiSelect with this:

export const OptimizedMultiSelect = memo(function OptimizedMultiSelect({
  id,
  label,
  values,
  onChange,
  placeholder = "Select options",
  required,
  options,
  readOnly = false,
  description,
  showValues = false,
}: OptimizedMultiSelectProps) {
  id = useFieldId(id);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleToggle = useCallback(
    (optionValue: string, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (readOnly) return;

      const newValues = values.includes(optionValue)
        ? values.filter((v) => v !== optionValue)
        : [...values, optionValue];
      onChange?.(newValues);
    },
    [values, onChange, readOnly],
  );

  const removeValue = useCallback(
    (valueToRemove: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (readOnly) return;
      onChange?.(values.filter((v) => v !== valueToRemove));
    },
    [values, onChange, readOnly],
  );

  const handleInputClick = useCallback(() => {
    if (!readOnly && !isOpen) {
      setIsOpen(true);
    }
  }, [readOnly, isOpen]);

  // Get selected labels
  const selectedLabels = values
    .map((value) => options.find((opt) => opt.value === value)?.label)
    .filter(Boolean);

  const selectedCount = selectedLabels.length;
  return (
    <div className="space-y-2" ref={containerRef}>
      <Label htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>

      <div className="relative">
        {/* Input field that shows selected items as badges */}
        <div
          className={`min-h-10 px-3 py-2 border rounded-md bg-white dark:bg-card flex flex-wrap gap-1.5 items-center transition-colors ${
            isOpen
              ? "border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900"
              : "border-slate-300 dark:border-border"
          } ${readOnly ? "cursor-not-allowed opacity-60 bg-slate-50 dark:bg-surface" : "cursor-text"}`}
          onClick={handleInputClick}
        >
          {selectedCount === 0 ? (
            <span className="text-slate-400 dark:text-muted-foreground text-sm">
              {placeholder}
            </span>
          ) : showValues ? (
            selectedLabels.map((label, index) => {
              const optionValue = values[index];
              return (
                <div
                  key={optionValue}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-brand/50 text-blue-800 dark:text-link text-sm font-medium rounded-md"
                >
                  <span className="truncate max-w-[150px]">{label}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={(e) => removeValue(optionValue, e)}
                      className="ml-0.5 p-0.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex-shrink-0"
                      title={`Remove ${label}`}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-sm font-medium ${selectedCount > 0 ? "text-slate-700 dark:text-foreground" : "text-slate-400 dark:text-muted-foreground"}`}
              >
                {/* {selectedCount === 0 ? placeholder : `${selectedCount} ${selectedCount === 1 ? 'item' : 'items'} selected`} */}
                {placeholder || "Items Selected"}
              </span>
              {selectedCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-brand/40 dark:text-link">
                  {selectedCount}
                </span>
              )}
            </div>
          )}

          {/* Hidden input for accessibility and focus management */}
          <input
            ref={inputRef}
            type="text"
            className="sr-only"
            id={id}
            readOnly
            tabIndex={readOnly ? -1 : 0}
            onFocus={() => !readOnly && setIsOpen(true)}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={`${id}-options`}
            aria-haspopup="listbox"
          />

          {/* Dropdown arrow indicator */}
          {!readOnly && (
            <div className="ml-auto flex-shrink-0 pl-2">
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Dropdown list with checkboxes */}
        {isOpen && !readOnly && (
          <div
            id={`${id}-options`}
            role="listbox"
            aria-multiselectable="true"
            className="absolute z-10 w-full mt-1 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg shadow-xl max-h-60 overflow-y-auto"
          >
            <div className="p-1">
              {options.map((option) => {
                const isSelected = values.includes(option.value);
                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-blue-50 dark:bg-brand/30 text-blue-900 dark:text-foreground"
                        : "hover:bg-slate-50 dark:hover:bg-accent text-slate-700 dark:text-muted-foreground"
                    }`}
                    onClick={(e) => handleToggle(option.value, e)}
                  >
                    <div
                      className={`flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
                        isSelected
                          ? "border-blue-500 bg-blue-500 dark:bg-primary dark:border-primary-border"
                          : "border-slate-300 dark:border-primary-border"
                      }`}
                    >
                      {isSelected && (
                        <svg
                          className="w-2.5 h-2.5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm">{option.label}</span>
                    {isSelected && (
                      <svg
                        className="w-4 h-4 ml-auto text-blue-500 dark:text-link"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {description && (
        <p className="text-sm text-slate-500 dark:text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );
});
